import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Shield, Cross, Activity } from "lucide-react";
import { IMAGES, CREDIBILITY } from "@/lib/data";
import { Reveal, MaskedLines, Counter, Eyebrow } from "@/components/site/Motion";

const STATS = [
  { value: 223, label: "FBI-designated active-shooter incidents, 2020–2024" },
  { value: 5, label: "Program components in the readiness framework" },
  { value: 18, label: "Months to independent pilot evaluation" },
  { value: 6, label: "Institutions in a founding regional pilot (3–6)" },
];

const PROBLEMS = [
  { num: "01", title: "Fragmented training objectives", text: "Tactical medical content appears in some agencies' training and not others, with no shared competency baseline." },
  { num: "02", title: "Inconsistent interagency handoffs", text: "Clinical and operational information is incompletely transferred at the point of transport or hospital arrival." },
  { num: "03", title: "Limited joint exercises", text: "Cooperation depends on individual relationships rather than documented, rehearsed responsibilities." },
  { num: "04", title: "Variable readiness measurement", text: "Agencies can report training hours, but not demonstrated, retained, interagency capability." },
  { num: "05", title: "Incomplete corrective-action follow-through", text: "After-action reports are produced, but corrective actions often go unassigned, unclosed, and untested." },
  { num: "06", title: "Uneven interagency integration", text: "Warm-zone access, common terminology, and unified command are unevenly applied across jurisdictions." },
];

const COMPONENTS = [
  { num: "01", title: "Common Readiness Crosswalk", text: "What \u201cready\u201d means, by role." },
  { num: "02", title: "Joint Scenario Curriculum", text: "How it is taught and rehearsed." },
  { num: "03", title: "Readiness Measurement", text: "What is counted, and how well." },
  { num: "04", title: "After-Action Learning", text: "What changes as a result." },
  { num: "05", title: "Train-the-Trainer & QA", text: "How quality survives scale." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-40 pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.05 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-12 bg-signal" />
            <Eyebrow>Proposed pilot framework · Pre-pilot status</Eyebrow>
          </motion.div>

          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight text-white leading-[1.02] max-w-5xl">
            <MaskedLines lines={["Building measurable", "interagency medical readiness", "for active-threat incidents."]} startDelay={0.15} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.8 }}
            className="mt-8 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl"
          >
            T-EMRP is a proposed implementation and evaluation framework that helps law enforcement, fire/rescue, EMS, dispatch, and hospital partners translate recognized medical doctrine into shared competencies, joint exercises, measurable readiness, and structured after-action improvement.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/contact" data-testid="hero-cta-primary" className="inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
              Request More Information <ArrowUpRight size={15} />
            </Link>
            <Link to="/framework" data-testid="hero-cta-secondary" className="inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
              Explore the Framework <ArrowRight size={15} />
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mt-8 font-mono text-[10px] uppercase tracking-[0.15em] text-mist max-w-xl leading-relaxed"
          >
            T-EMRP does not replace existing clinical standards, local protocols, medical direction, or incident-command doctrine.
          </motion.p>
        </div>

        {/* STAT STRIP */}
        <div className="relative border-t border-line/60 backdrop-blur-sm bg-ink/40 mt-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className={`py-8 pr-6 ${i > 0 ? "lg:border-l lg:border-line/60 lg:pl-8" : ""}`}>
                <Counter value={s.value} className="font-mono text-4xl lg:text-5xl font-bold text-white" />
                <p className="mt-2 text-xs text-mist leading-relaxed">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREDIBILITY MARQUEE */}
      <section className="border-y border-line bg-surface py-6 overflow-hidden" data-testid="credibility-marquee">
        <Marquee speed={30} gradient={false} autoFill>
          {CREDIBILITY.map((c, i) => (
            <span key={i} className="flex items-center gap-8 mx-8">
              <span className="font-mono text-sm uppercase tracking-[0.25em] text-slate-400">{c}</span>
              {i % 3 === 0 ? <Shield size={13} className="text-signal" /> : i % 3 === 1 ? <Cross size={13} className="text-signal" /> : <Activity size={13} className="text-signal" />}
            </span>
          ))}
        </Marquee>
      </section>

      {/* CHAPTER 01 — THE CHALLENGE */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="challenge-section">
        <Reveal>
          <div className="flex items-center gap-4 mb-6">
            <span className="font-mono text-xs text-mist">Chapter 01</span>
            <span className="h-px w-10 bg-signal" />
            <Eyebrow>The challenge</Eyebrow>
          </div>
          <h2 className="font-display font-extrabold text-base md:text-lg text-white tracking-tight sm:text-4xl lg:text-5xl leading-[1.05] max-w-3xl" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
            The gap is implementation, not doctrine.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">
            The United States does not lack tactical medical guidance. Agencies that must respond together to an active-threat incident — police, fire, EMS, dispatch, and hospitals — frequently do not train together, exercise together, or measure readiness the same way.
          </p>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
          {PROBLEMS.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.06} className="bg-ink">
              <div data-testid={`problem-card-${p.num}`} className="group p-8 lg:p-10 h-full hover:bg-surface transition-colors duration-300">
                <span className="font-mono text-5xl font-bold text-outline group-hover:text-signal transition-colors duration-300">{p.num}</span>
                <h3 className="mt-6 font-display font-bold text-xl text-white tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EDITORIAL IMAGE BAND */}
      <ParallaxBand />

      {/* CHAPTER 02 — FIVE COMPONENTS */}
      <section className="py-24 lg:py-32 bg-surface border-y border-line" data-testid="components-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-start">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono text-xs text-mist">Chapter 02</span>
                <span className="h-px w-10 bg-signal" />
                <Eyebrow>What T-EMRP does</Eyebrow>
              </div>
              <h2 className="font-display font-extrabold text-white tracking-tight leading-[1.05]" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
                Five components,<br />one closed-loop method.
              </h2>
              <div className="mt-10 relative overflow-hidden border border-line">
                <img src={IMAGES.simulation} alt="Instructor demonstrating hemorrhage control in a simulation center" className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700" />
                <span className="absolute bottom-0 left-0 bg-ink/80 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 px-4 py-2">
                  Joint scenario curriculum · Simulation
                </span>
              </div>
              <Link to="/framework" data-testid="components-cta" className="mt-10 inline-flex items-center gap-3 border border-slate-500 hover:border-signal hover:text-signal text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Explore the full framework <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div>
            {COMPONENTS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div data-testid={`component-item-${c.num}`} className="group flex items-baseline gap-8 border-b border-line py-8 hover:pl-4 transition-[padding] duration-300">
                  <span className="font-mono text-sm text-signal">{c.num}</span>
                  <div>
                    <h3 className="font-display font-bold text-2xl lg:text-3xl text-white tracking-tight group-hover:text-signal transition-colors">{c.title}</h3>
                    <p className="mt-2 text-sm text-slate-400">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CHAPTER 03 — PILOT TEASER */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="pilot-teaser">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden border border-line">
              <img src={IMAGES.commandCenter} alt="Emergency dispatch and incident command center" className="w-full h-[420px] object-cover hover:scale-105 transition-transform duration-700" />
              <span className="absolute bottom-0 left-0 bg-ink/80 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 px-4 py-2">
                Dispatch · Common terminology · Timestamps
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-xs text-mist">Chapter 03</span>
              <span className="h-px w-10 bg-signal" />
              <Eyebrow>The pilot</Eyebrow>
            </div>
            <h2 className="font-display font-extrabold text-white tracking-tight leading-[1.05]" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
              An 18-month, independently evaluated regional pilot.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              The pilot exists to answer one question: can a shared readiness framework be implemented, measured reliably, retained over time, and accepted by the institutions that would have to live with it?
            </p>
            <ul className="mt-8 space-y-3">
              {["3–6 founding institutions across six functional roles", "Documented stage gates every phase", "Independent evaluator controls the analysis", "Bounded, reversible participation"].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                  <span className="font-mono text-signal text-xs mt-0.5">→</span> {t}
                </li>
              ))}
            </ul>
            <Link to="/pilot" data-testid="pilot-teaser-cta" className="mt-10 inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
              See the Pilot Model <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-line bg-surface py-24 lg:py-32 relative overflow-hidden" data-testid="home-cta">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${IMAGES.jointExercise})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/70 to-surface/90" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-left">
          <Reveal>
            <Eyebrow>Learn more</Eyebrow>
            <h2 className="mt-6 font-display font-extrabold text-white tracking-tight leading-[1.02]" style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}>
              Want the full picture?<br />Request more information.
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="home-cta-contact" className="inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Request More Information <ArrowUpRight size={15} />
              </Link>
              <Link to="/partnerships" data-testid="home-cta-partnerships" className="inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Partnership Options <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

const ParallaxBand = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} className="relative h-[60vh] overflow-hidden border-y border-line" data-testid="parallax-band">
      <motion.div className="absolute inset-[-15%]" style={{ y }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.jointExercise})` }} />
      </motion.div>
      <div className="absolute inset-0 bg-ink/60" />
      <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex items-end pb-16">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-4">Interagency · Joint exercise</p>
          <p className="font-display font-bold text-2xl md:text-4xl text-white tracking-tight max-w-3xl leading-tight">
            "Cooperation cannot depend on individual relationships. It must be documented, rehearsed, and measured."
          </p>
        </Reveal>
      </div>
    </section>
  );
};
